import { FC, Fragment, useContext, useEffect, useRef, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/solid";
import FilterCategoryTitle from "../filterCategoryTitle";
import classNames from "../../utils/twClassNames";
import { PickerValue } from "../../types";
import { TranslationContext } from "../../context/TranslationContext";

type Props = {
  updateFromDefault?: boolean;
  disabledIds?: (number | string)[];
  label?: string;
  values: Array<PickerValue>;
  defaultValueId?: number;
  selectedValueIds: string[];

  onChange?: (value: any) => void;
};

const StyledMultiselectDropdown: FC<Props> = ({
  disabledIds,
  label,
  values,
  selectedValueIds,

  onChange,
}) => {
  const { i } = useContext(TranslationContext);

  const [selectedValues, setSelectedValues] = useState(
    (values ?? []).length > 0
      ? selectedValueIds.map((id) => (values ?? []).find((x) => x.id == id))
      : []
  );

  useEffect(() => {
    if (onChange) onChange(selectedValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValues]);

  // Sync selectedValues when values arrive from API (async load)
  const initialized = useRef(false);
  useEffect(() => {
    if (values && values.length > 0 && !initialized.current) {
      initialized.current = true;
      const initial = selectedValueIds
        .map((id) => values.find((x) => x.id == id))
        .filter(Boolean) as PickerValue[];
      setSelectedValues(initial.length > 0 ? initial : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  if (!values || values.length < 1) {
    return null;
  }

  // const handleSelectionChange = (values: PickerValue[]) => {
  //   if (values.length === 0 || (values.length === 1 && values[0].id === "-2")) {
  //     setSelectedValues([{ id: "-2", name: "All" }]);
  //     return;
  //   }

  //   if (values.some((x) => x.id === "-2")) {
  //     values = values.filter((x) => x.id !== "-2");
  //   }

  //   setSelectedValues(values);
  // };

  return (
    <Listbox
      multiple={true}
      value={values}
      onChange={(e) => {
        // handleSelectionChange(e);
        // // setSelectedValues(e);
        // if (onChange) onChange(e);
      }}
    >
      {({ open }) => (
        <>
          {label && (
            <Listbox.Label>
              <FilterCategoryTitle title={label} />
            </Listbox.Label>
          )}
          <div className="relative">
            <Listbox.Button
              data-testid={`dropdown-button-for-${label}`}
              className="bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-6 py-2 text-left cursor-default sm:text-base text-sm"
            >
              {/* <span className="block truncate text-custom-black-200">
                  {selected.name}
                </span>
      */}
              <span className="block truncate text-custom-black">
                {selectedValues
                  .filter((x) => x?.name)
                  .map((x) => x!.name)
                  .join(", ")}
              </span>

              <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <ChevronDownIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options
                data-testid={`dropdown-options-for-${label}`}
                className="absolute z-40 mt-1 w-full bg-white shadow-lg max-h-60 rounded py-1 text-base focus:outline-none overflow-auto sm:text-sm"
              >
                {values.map((value) => (
                  <Listbox.Option
                    onClick={() => {
                      let newValues: (PickerValue | undefined)[] = [];
                      if (value.id == "-2") {
                        newValues = [{ id: "-2", name: i("All") }];
                      } else if (
                        selectedValues.some((x) => x?.id === value.id)
                      ) {
                        newValues = selectedValues.filter(
                          (v) => v !== undefined && v.id !== value.id
                        );
                      } else {
                        newValues = [
                          ...selectedValues.filter((v) => v && v.id != "-2"),
                          value,
                        ];
                      }
                      if (newValues.length === 0) {
                        newValues = [{ id: "-2", name: i("All") }];
                      }
                      setSelectedValues(newValues);
                    }}
                    disabled={disabledIds?.includes(value.id)}
                    key={value.id}
                    data-testid={`dropdown-option-${value.name}`}
                    className={({ active, disabled }) =>
                      classNames(
                        disabled && "opacity-30",
                        active
                          ? "text-custom-blue bg-custom-light-blue bg-opacity-20"
                          : "text-custom-blue",
                        "cursor-default select-none focus:outline-none relative py-2 pl-3 pr-6"
                      )
                    }
                    value={value}
                  >
                    <span className="font-normal block truncate">
                      {value.name}
                    </span>
                    {selectedValues.map((v) => v?.id).includes(value.id) && (
                      <span className="text-custom-blue absolute inset-y-0 right-0 flex items-center pr-2">
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  );
};

export default StyledMultiselectDropdown;
